import { inject, Injectable } from '@angular/core';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { merge, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { AttachmentsActions, AttachmentsSelectors } from '@core/attachments-data';
import type { Attachment } from '@core/attachments-types';
import { provideAttachmentPdfRefreshToken } from '@core/attachments-types';
import { filterBoolean } from '@core/common-legacy';
import { getSignedUrlTimeRemainingMs } from '@core/time-legacy';

@Injectable({ providedIn: 'root' })
export class AttachmentActionsService {
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  public uploadAttachment(file: File, uuid: string) {
    this.store.dispatch(
      AttachmentsActions.uploadAttachmentPessimistic({
        payload: {
          file,
          correlationId: uuid,
        },
      }),
    );
  }

  public getAttachment(uuid: string) {
    return this.store.select(AttachmentsSelectors.byId(uuid)).pipe(filterBoolean);
  }

  public uploadAttachmentSuccessAction(uuid: string) {
    return this.actions$.pipe(
      ofType(AttachmentsActions.uploadAttachmentPessimisticFailed),
      filter(({ payload }) => uuid === payload.correlationId),
    );
  }

  public fetchFreshAttachment(attachment: Attachment) {
    const remaining = attachment.pdfUrl ? getSignedUrlTimeRemainingMs(attachment.pdfUrl) : null;
    if (remaining !== null && remaining > PDF_URL_MIN_REMAINING_MS) {
      return of(attachment);
    }

    this.store.dispatch(AttachmentsActions.fetchOne({ payload: { id: attachment.id } }));

    const success$ = this.actions$.pipe(
      ofType(AttachmentsActions.fetchOneSuccess),
      filter(({ payload }) => payload.data.id === attachment.id),
      map(({ payload }) => <Attachment>payload.data),
    );

    const failure$ = this.actions$.pipe(
      ofType(AttachmentsActions.fetchOneFailed),
      filter(({ payload }) => payload.id === attachment.id),
      map(() => null),
    );

    return merge(success$, failure$).pipe(take(1));
  }
}

const PDF_URL_MIN_REMAINING_MS = 30_000;

export function provideAttachmentPdfRefresh() {
  return provideAttachmentPdfRefreshToken({
    useFactory: () => {
      const service = inject(AttachmentActionsService);
      return (attachment: Attachment) => service.fetchFreshAttachment(attachment);
    },
  });
}
